<template>
  <div>
    <div class="flex flex-wrap py-16 m-0">
      <div
        v-for="(step, index) in steps"
        :key="step.name"
        class="step relative flex-col flex-1 md:flex-row md:flex-none flex items-center gap-8"
        :class="{
          current: index + 1 == currentStep,
          prev: index + 1 < currentStep,
        }"
        :disabled="index + 1 == currentStep ? '' : 'disabled'"
        :aria-disabled="index + 1 == currentStep ? 'false' : 'true'"
        role="button"
        tabindex="0"
        @click="onStepCounterClick(index + 1)"
        @keyup.enter.self.prevent="onStepCounterClick(index + 1)"
        @keyup.space.self.prevent="onStepCounterClick(index + 1)"
      >
        <span
          class="number heading-xxs w-40 z-10 flex items-center justify-center rounded-full border-2 border-solid border-border-disabled content-none flex-[0_0_4rem] h-40 bg-surface-disabled text-text-disabled"
        >
          <template v-if="index + 1 < currentStep">
            <span class="visuallyhidden">Färdigt steg</span>
          </template>
          <template v-else-if="index + 1 === currentStep">
            <span class="visuallyhidden">Pågående steg</span>
          </template>
          <template v-else-if="index + 1 > currentStep">
            <span class="visuallyhidden">Kommande steg</span>
          </template>

          <span v-if="index + 1 < currentStep">
            <nuxt-icon filled name="check-step" aria-hidden="true" />
          </span>
          <span v-else>
            <span>{{ index + 1 }}</span>
          </span>
        </span>
        <span class="text heading-xxs z-10 md:py-9 md:px-8 text-text-disabled">
          {{ step.name }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
const checkoutStore = useMyCheckoutStore();

export type StepIndicatorStep = {
  name: string;
};

interface Props {
  currentStep: number;
  steps: StepIndicatorStep[];
}

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<{
  (e: 'stepCounter', step: number): void;
}>();

const onStepCounterClick = (step: number) => {
  if (!checkoutStore.isCartEmpty && props.currentStep > step) {
    emit('stepCounter', step);
  }
};
</script>

<style scoped lang="scss">
.step:disabled:not(:last-child):after {
  content: '';
  background-color: var(--border-disabled) !important;
}
.step {
  &:not(:last-child) {
    flex: 1;
  }

  &:not(:last-child):after {
    content: '';
    flex: 1;
    height: 2px;
    background-color: var(--border-primary);
    margin-inline-end: 0;
  }
  &:disabled:not(:last-child):after {
    content: '';
    background-color: var(--border-disabled) !important;
  }

  &.prev {
    .number {
      background-color: var(--surface-success);
      border: 0.2rem solid var(--border-primary);
      color: var(--text-on-highlight);
      cursor: pointer;
    }
    .text {
      color: var(--text-primary);
      cursor: pointer;
    }
  }
  &.current {
    .number {
      background-color: var(--surface-secondary);
      border: 0.2rem solid var(--border-primary);
      color: var(--text-on-highlight);
    }
    .text {
      color: var(--text-primary);
    }
  }

  @include mq('md') {
    &:last-child {
      flex: 0.5;
    }
    &:first-child {
      align-items: flex-start;
      flex: 0.5;
      .number {
        margin-left: 1.8rem;
      }
    }
    &:not(:last-child):after {
      position: absolute;
      content: '';
      width: 100%;
      top: 2rem;
      z-index: 1;
      left: 20%;
    }
    &:first-child:after {
      left: 40%;
    }
  }
}
</style>
